import { useCallback, useEffect, useState } from "react";
import type { CurrentUser } from "@/lib/auth";
import { userHasGroup } from "@/lib/auth";
import { fetchErrorLogSummary, type ErrorLogSummary } from "@/lib/errorLogsApi";

const POLL_INTERVAL_MS = 60_000;

/**
 * Geteilter Modul-Store: Header-Indicator, Admin-Statuskarte usw. teilen sich
 * EINEN Fetch + EINEN 60s-Timer (pausiert bei verstecktem Tab), statt dass
 * jede Komponente selbst `/admin/error-logs/summary` pollt.
 */
type Listener = (summary: ErrorLogSummary | null, loading: boolean) => void;

let sharedSummary: ErrorLogSummary | null = null;
let sharedLoading = false;
const listeners = new Set<Listener>();
let pollTimer: number | null = null;
let inflight: Promise<void> | null = null;

function emit(): void {
  for (const fn of listeners) {
    try {
      fn(sharedSummary, sharedLoading);
    } catch {
      /* ignore */
    }
  }
}

function refreshShared(): Promise<void> {
  if (inflight) return inflight;
  sharedLoading = true;
  emit();
  inflight = fetchErrorLogSummary()
    .then((data) => {
      sharedSummary = data;
    })
    .catch(() => {
      // Fehler beim Monitoring sollen die App nicht blockieren.
      sharedSummary = null;
    })
    .finally(() => {
      sharedLoading = false;
      inflight = null;
      emit();
    });
  return inflight;
}

function ensurePolling(): void {
  if (pollTimer != null) return;
  pollTimer = window.setInterval(() => {
    if (document.visibilityState === "hidden") return;
    if (listeners.size === 0) return;
    void refreshShared();
  }, POLL_INTERVAL_MS);
}

function stopPollingIfIdle(): void {
  if (listeners.size === 0 && pollTimer != null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
    sharedSummary = null;
  }
}

export function useErrorLogSummary(user: CurrentUser | null) {
  const isAdmin = userHasGroup(user, "App-Admins");
  const [summary, setSummary] = useState<ErrorLogSummary | null>(isAdmin ? sharedSummary : null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAdmin) {
      setSummary(null);
      return;
    }
    await refreshShared();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setSummary(null);
      return;
    }
    const listener: Listener = (next, busy) => {
      setSummary(next);
      setLoading(busy);
    };
    listeners.add(listener);
    listener(sharedSummary, sharedLoading);
    ensurePolling();
    if (!sharedSummary && !inflight) void refreshShared();
    return () => {
      listeners.delete(listener);
      stopPollingIfIdle();
    };
  }, [isAdmin]);

  return { summary, loading, isAdmin, refresh };
}
